/**
 * Sidebar-Navigation mit Menüpunkten
 */

import React from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Build as BuildIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  ListAlt as ListAltIcon,
  PendingActions as PendingActionsIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Euro as EuroIcon,
  Link as LinkIcon,
  Analytics as AnalyticsIcon,
  VpnKey as ApiKeyIcon,
  Upload as UploadIcon,
  Chat as ChatIcon,
  SmartToy as SmartToyIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { UserRole } from "../../types/api-types";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  children?: NavItem[];
  requiredRoles?: UserRole[]; // Neue Eigenschaft für Rollenbeschränkung
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: 280,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    boxShadow: "4px 0 12px rgba(0,0,0,0.15)",
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  margin: "4px 12px",
  borderRadius: "12px",
  color: "rgba(255,255,255,0.8)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "white",
    transform: "translateX(4px)",
  },
  "&.active": {
    backgroundColor: "rgba(255,255,255,0.15)",
    color: "white",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
    },
  },
}));

const StyledListItemIcon = styled(ListItemIcon)({
  color: "inherit",
  minWidth: "40px",
});

const navigationItems: NavItem[] = [
  {
    path: "/dashboard",
    label: "Dashboard",
    icon: <DashboardIcon />,
  },
  {
    path: "/profile",
    label: "Profil",
    icon: <PersonIcon />,
  },
  {
    path: "/rental-objects",
    label: "Verwaltung Mietobjete",
    icon: <BuildIcon />,
    children: [
      { path: "/rental-objects", label: "Mietobjekte", icon: <BuildIcon /> },
      {
        path: "/articles/bundles",
        label: "Bundle-Verwaltung",
        icon: <LinkIcon />,
      },
    ],
  },
  {
    path: "/customers",
    label: "Kunden",
    icon: <PeopleIcon />,
  },
  {
    path: "/articles",
    label: "Artikel",
    icon: <InventoryIcon />,
  },
  {
    path: "/invoices",
    label: "Rechnungen",
    icon: <ReceiptIcon />,
  },
  {
    path: "/revenue-analytics",
    label: "Umsatzanalyse",
    icon: <AnalyticsIcon />,
    requiredRoles: [UserRole.ADMIN, UserRole.MANAGER],
  },
  {
    path: "/settings",
    label: "Einstellungen",
    icon: <SettingsIcon />,
    children: [
      {
        path: "/settings/company",
        label: "Unternehmen",
        icon: <BusinessIcon />,
      },
      {
        path: "/settings/users",
        label: "Benutzer",
        icon: <PersonIcon />,
        requiredRoles: [UserRole.ADMIN, UserRole.MANAGER],
      },
      { path: "/settings/pricing", label: "Preise", icon: <EuroIcon /> },
      {
        path: "/settings/business",
        label: "Geschäftsstatus",
        icon: <BusinessIcon />,
      },
      {
        path: "/settings/api-keys",
        label: "API-Schlüssel",
        icon: <ApiKeyIcon />,
        requiredRoles: [UserRole.ADMIN],
      },
      {
        path: "/settings/chat",
        label: "Chat",
        icon: <ChatIcon />,
        requiredRoles: [UserRole.ADMIN],
      },
      {
        path: "/settings/blocked-contacts",
        label: "Blockierte Kontakte",
        icon: <BlockIcon />,
        requiredRoles: [UserRole.ADMIN, UserRole.MANAGER],
      },
      {
        path: "/settings/ai",
        label: "KI",
        icon: <SmartToyIcon />,
        requiredRoles: [UserRole.ADMIN],
      },
      {
        path: "/settings/system",
        label: "System",
        icon: <SettingsIcon />,
        requiredRoles: [UserRole.ADMIN],
      },
      {
        path: "/settings/legacy-import",
        label: "Legacy Import",
        icon: <UploadIcon />,
        requiredRoles: [UserRole.ADMIN],
      },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const { user } = useAuth();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const handleMobileClose = () => {
    if (isMobile) {
      onToggle();
    }
  };

  // On desktop, sidebar should be persistent/permanent
  // On mobile, sidebar should be a drawer

  // Check if user has required role for nav item
  const hasRequiredRole = (item: NavItem): boolean => {
    if (!item.requiredRoles || item.requiredRoles.length === 0) {
      return true; // No role restriction
    }
    if (!user || !user.role) {
      return false; // No user or role
    }
    return item.requiredRoles.includes(user.role);
  };

  const renderNavItem = (item: NavItem, level = 0) => {
    // Check role permission
    if (!hasRequiredRole(item)) {
      return null;
    }

    const isActive = location.pathname === item.path;
    const isExpanded = expandedItems.includes(item.path);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <Box key={item.path}>
        <ListItem disablePadding sx={{ display: "block" }}>
          {hasChildren ? (
            <StyledListItemButton
              onClick={() => toggleExpanded(item.path)}
              className={isActive ? "active" : ""}
              sx={{ pl: level * 2 + 2 }}
            >
              <StyledListItemIcon>{item.icon}</StyledListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: level > 0 ? "0.875rem" : "1rem",
                  fontWeight: isActive ? 600 : 500,
                }}
              />
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </StyledListItemButton>
          ) : (
            <Box
              component={Link}
              to={item.path}
              sx={{ textDecoration: "none" }}
            >
              <StyledListItemButton
                onClick={handleMobileClose}
                className={isActive ? "active" : ""}
                sx={{ pl: level * 2 + 2 }}
              >
                <StyledListItemIcon>{item.icon}</StyledListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: level > 0 ? "0.875rem" : "1rem",
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              </StyledListItemButton>
            </Box>
          )}
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderNavItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo/Brand Area */}
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          mb: 1,
        }}
      >
        {isMobile ? (
          <Box sx={{ fontSize: "1.5rem" }}>🏗️</Box>
        ) : (
          <Box>
            <Box sx={{ fontSize: "1.5rem", fontWeight: "bold", mb: 0.5 }}>
              🏗️ Mietwerkstatt
            </Box>
            <Box sx={{ fontSize: "0.75rem", opacity: 0.7 }}>
              Vermietungsmanagement
            </Box>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, pt: 0 }}>
        {navigationItems.map((item) => renderNavItem(item))}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <Box sx={{ fontSize: "0.75rem", opacity: 0.6, textAlign: "center" }}>
          © 2025 Mietwerkstatt
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={isOpen}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280, // Gleiche Breite wie Desktop für bessere Lesbarkeit
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
          },
        }}
      >
        {drawer}
      </Drawer>
    );
  }

  // Desktop version - always persistent, no toggle
  return (
    <StyledDrawer variant="permanent" open={true}>
      {drawer}
    </StyledDrawer>
  );
};

export default Sidebar;
